import {
  Mic, MicOff, Video, VideoOff,
} from 'lucide-react-native';
import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Platform, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonText, Center, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';

import { attachPreviewStream } from './attachPreviewStream';
import type { MeetingJoinSettings } from './types';

type MediaDeviceInfo = { deviceId: string; label: string };

type MeetingLobbyProps = {
  onJoin: (settings: MeetingJoinSettings) => void;
};

function siteHostForPermissionHelp(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return 'this site';
}

function trackDeviceId(stream: MediaStream, kind: 'audio' | 'video'): string | undefined {
  const track = kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
  const deviceId = track?.getSettings?.().deviceId;
  return deviceId || undefined;
}

function listedOrFirst(
  resolved: string | undefined,
  devices: MediaDeviceInfo[],
): string | undefined {
  if (resolved && devices.some((device) => device.deviceId === resolved)) {
    return resolved;
  }
  return devices[0]?.deviceId;
}

/* eslint-disable react-native/no-color-literals */
const PREVIEW_HEIGHT = 360;
const PREVIEW_STYLE = {
  width: '100%' as const,
  height: PREVIEW_HEIGHT,
  objectFit: 'cover' as const,
  display: 'block' as const,
  backgroundColor: '#0e1218',
};
/* eslint-enable react-native/no-color-literals */

function DeviceSelect({
  label, devices, value, onChange, testID,
}: {
  label: string;
  devices: MediaDeviceInfo[];
  value?: string;
  onChange: (id: string) => void;
  testID: string;
}) {
  return (
    <VStack space="xs">
      <Text size="xs" color="$textSubtle" fontWeight="semibold">
        {label}
      </Text>
      <VStack
        borderWidth={1}
        borderColor="$borderLight"
        borderRadius="$md"
        overflow="hidden"
        testID={testID}
      >
        {devices.map((d) => {
          const selected = d.deviceId === value;
          return (
            <Pressable
              key={d.deviceId}
              onPress={() => onChange(d.deviceId)}
              testID={`${testID}-${d.deviceId}`}
              accessibilityRole="button"
              accessibilityState={{ selected }}
            >
              <Box
                px="$3"
                py="$2"
                bg={selected ? '$bgSubtle' : '$bgApp'}
              >
                <Text size="sm" color="$textBody" numberOfLines={1}>
                  {d.label || d.deviceId}
                </Text>
              </Box>
            </Pressable>
          );
        })}
      </VStack>
    </VStack>
  );
}

/**
 * Pre-join lobby: camera preview, device selection, mute toggles, join action.
 * On web, camera and microphone permission is requested only after the user opts in.
 */
export function MeetingLobby({ onJoin }: MeetingLobbyProps) {
  const { t } = useTranslation();
  const isWeb = Platform.OS === 'web';
  const siteHost = useMemo(() => siteHostForPermissionHelp(), []);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const skipPreviewEffectOnceRef = useRef(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

  const [mediaGranted, setMediaGranted] = useState(!isWeb);
  const [mediaBlocked, setMediaBlocked] = useState(false);
  const [requestInFlight, setRequestInFlight] = useState(false);
  const [showMediaGuidance, setShowMediaGuidance] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDeviceId, setAudioDeviceId] = useState<string | undefined>();
  const [videoDeviceId, setVideoDeviceId] = useState<string | undefined>();
  const [inferredAudioDeviceId, setInferredAudioDeviceId] = useState<string | undefined>();
  const [inferredVideoDeviceId, setInferredVideoDeviceId] = useState<string | undefined>();
  const selectedAudioDeviceId = audioDeviceId ?? inferredAudioDeviceId;
  const selectedVideoDeviceId = videoDeviceId ?? inferredVideoDeviceId;
  const showControls = !isWeb || (mediaGranted && !mediaBlocked);

  const stopPreview = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    setPreviewStream(null);
  }, []);

  const refreshDevices = useCallback(async (): Promise<{
    audio: MediaDeviceInfo[];
    video: MediaDeviceInfo[];
  }> => {
    if (!isWeb || !navigator.mediaDevices?.enumerateDevices) {
      return { audio: [], video: [] };
    }
    const all = await navigator.mediaDevices.enumerateDevices();
    const audio = all.filter((d) => d.kind === 'audioinput').map((d) => ({
      deviceId: d.deviceId, label: d.label,
    }));
    const video = all.filter((d) => d.kind === 'videoinput').map((d) => ({
      deviceId: d.deviceId, label: d.label,
    }));
    setAudioDevices(audio);
    setVideoDevices(video);
    return { audio, video };
  }, [isWeb]);

  const requestMediaAccess = useCallback(async (): Promise<boolean> => {
    if (!isWeb) {
      setMediaGranted(true);
      return true;
    }
    setRequestInFlight(true);
    setMediaBlocked(false);
    setShowMediaGuidance(false);
    try {
      stopPreview();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      streamRef.current = stream;
      setPreviewStream(stream);
      const { audio, video } = await refreshDevices();
      setInferredVideoDeviceId(listedOrFirst(trackDeviceId(stream, 'video'), video));
      setInferredAudioDeviceId(listedOrFirst(trackDeviceId(stream, 'audio'), audio));
      setMicOn(true);
      setCamOn(true);
      skipPreviewEffectOnceRef.current = true;
      setMediaGranted(true);
      return true;
    } catch {
      setMediaBlocked(true);
      return false;
    } finally {
      setRequestInFlight(false);
    }
  }, [isWeb, refreshDevices, stopPreview]);

  const handleEnableMedia = useCallback(() => {
    requestMediaAccess().catch(() => undefined);
  }, [requestMediaAccess]);

  const handleRetryMedia = useCallback(() => {
    requestMediaAccess().catch(() => undefined);
  }, [requestMediaAccess]);

  // After grant, restart preview when the selected video device or cam toggle changes.
  useEffect(() => {
    if (!isWeb || !mediaGranted) return undefined;
    if (skipPreviewEffectOnceRef.current) {
      skipPreviewEffectOnceRef.current = false;
      return undefined;
    }
    if (!camOn) {
      stopPreview();
      return undefined;
    }
    let cancelled = false;
    const start = async () => {
      try {
        stopPreview();
        const vidStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: videoDeviceId ? { deviceId: videoDeviceId } : true,
        });
        if (cancelled) { vidStream.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = vidStream;
        setPreviewStream(vidStream);
        const { video } = await refreshDevices();
        if (cancelled) return;
        setInferredVideoDeviceId(listedOrFirst(trackDeviceId(vidStream, 'video'), video));
      } catch {
        setMediaBlocked(true);
      }
    };
    start();
    return () => { cancelled = true; };
  }, [camOn, isWeb, mediaGranted, refreshDevices, stopPreview, videoDeviceId]);

  const previewVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (!node || !previewStream || !mediaGranted || mediaBlocked || !camOn) return;
    attachPreviewStream(node, previewStream);
  }, [camOn, mediaBlocked, mediaGranted, previewStream]);

  // Re-attach when the stream changes while the preview element stays mounted.
  useLayoutEffect(() => {
    if (!isWeb || !mediaGranted || mediaBlocked || !camOn || !previewStream) return;
    attachPreviewStream(videoRef.current, previewStream);
  }, [camOn, isWeb, mediaBlocked, mediaGranted, previewStream]);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const handleJoin = () => {
    stopPreview();
    onJoin({
      audioDeviceId: selectedAudioDeviceId,
      videoDeviceId: selectedVideoDeviceId,
      microphoneEnabled: micOn,
      cameraEnabled: camOn,
    });
  };

  const introText = (() => {
    if (isWeb && mediaBlocked) return null;
    if (isWeb && !mediaGranted) return t('meeting.lobbyPrimingBody');
    return t('meeting.lobbyIntro');
  })();

  const blockedRecovery = isWeb && mediaBlocked ? (
    <VStack space="md" testID="meeting-lobby-blocked">
      <Text size="md" color="$statusError">
        {t('meeting.blockedLead')}
      </Text>
      <Text size="sm" color="$textBody">
        {t('meeting.blockedFix')}
      </Text>
      {showMediaGuidance ? (
        <VStack space="sm">
          <Text size="sm" color="$textSubtle">
            {t('oralExam.mediaBlockedHelpChrome', { host: siteHost })}
          </Text>
          <Text size="sm" color="$textSubtle">
            {t('oralExam.mediaBlockedHelpSafari', { host: siteHost })}
          </Text>
        </VStack>
      ) : null}
      <HStack space="md" flexWrap="wrap">
        <Button size="sm" onPress={handleRetryMedia} testID="meeting-lobby-retry">
          <ButtonText>{t('meeting.tryAgain')}</ButtonText>
        </Button>
        <Button
          size="sm"
          variant="outline"
          onPress={() => setShowMediaGuidance(true)}
          testID="meeting-lobby-more-help"
        >
          <ButtonText>{t('meeting.moreHelp')}</ButtonText>
        </Button>
      </HStack>
      <Text size="sm" color="$textSubtle">
        {t('meeting.blockedConsequence')}
      </Text>
    </VStack>
  ) : null;

  return (
    <Box flex={1} bg="$bgApp" justifyContent="center" testID="meeting-lobby">
      <VStack space="2xl" maxWidth={960} alignSelf="center" width="100%" px="$6" py="$8">
        <VStack space="xs">
          <Text size="2xl" color="$textHeading" fontWeight="semibold">
            {t('meeting.lobbyTitle')}
          </Text>
          {introText ? (
            <Text size="md" color="$textBody">
              {introText}
            </Text>
          ) : null}
        </VStack>

        {blockedRecovery}

        <HStack space="2xl" flexWrap="wrap" alignItems="flex-start">
          <Box
            flex={1}
            minWidth={320}
            borderRadius="$xl"
            overflow="hidden"
            bg="$bgDark"
            softShadow="1"
          >
            {isWeb && camOn && mediaGranted && !mediaBlocked ? (
              <Box testID="meeting-lobby-preview-video" width="100%">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  ref={previewVideoRef as never}
                  muted
                  playsInline
                  style={PREVIEW_STYLE}
                />
              </Box>
            ) : (
              <Center height={PREVIEW_HEIGHT} bg="$bgDark">
                <Text size="sm" color="$textHint">{t('meeting.cameraOffPreview')}</Text>
              </Center>
            )}
          </Box>

          <VStack space="lg" width={320} minWidth={280}>
            {isWeb && !mediaGranted && !mediaBlocked ? (
              <Button
                size="lg"
                action="primary"
                onPress={handleEnableMedia}
                isDisabled={requestInFlight}
                testID="meeting-lobby-enable"
              >
                <ButtonText>
                  {requestInFlight ? t('meeting.waiting') : t('meeting.enableButton')}
                </ButtonText>
              </Button>
            ) : null}

            {showControls ? (
              <>
                <HStack space="md">
                  <Pressable onPress={() => setMicOn((v) => !v)} testID="lobby-mic-toggle">
                    <Box
                      px="$3"
                      py="$2"
                      borderRadius="$full"
                      borderWidth={1}
                      borderColor="$borderLight"
                      bg={micOn ? '$bgApp' : '$statusErrorSubtle'}
                    >
                      <Icon as={micOn ? Mic : MicOff} size="sm" color="$textHeading" />
                    </Box>
                  </Pressable>
                  <Pressable onPress={() => setCamOn((v) => !v)} testID="lobby-cam-toggle">
                    <Box
                      px="$3"
                      py="$2"
                      borderRadius="$full"
                      borderWidth={1}
                      borderColor="$borderLight"
                      bg={camOn ? '$bgApp' : '$statusErrorSubtle'}
                    >
                      <Icon as={camOn ? Video : VideoOff} size="sm" color="$textHeading" />
                    </Box>
                  </Pressable>
                </HStack>

                {isWeb && audioDevices.length > 0 ? (
                  <DeviceSelect
                    label={t('meeting.microphone')}
                    devices={audioDevices}
                    value={selectedAudioDeviceId}
                    onChange={setAudioDeviceId}
                    testID="lobby-audio-devices"
                  />
                ) : null}
                {isWeb && videoDevices.length > 0 ? (
                  <DeviceSelect
                    label={t('meeting.camera')}
                    devices={videoDevices}
                    value={selectedVideoDeviceId}
                    onChange={setVideoDeviceId}
                    testID="lobby-video-devices"
                  />
                ) : null}
              </>
            ) : null}

            <Button
              size="lg"
              action="primary"
              onPress={handleJoin}
              isDisabled={isWeb && (!mediaGranted || mediaBlocked)}
              testID="meeting-lobby-join"
            >
              <ButtonText>{t('meeting.join')}</ButtonText>
            </Button>
          </VStack>
        </HStack>
      </VStack>
    </Box>
  );
}
